/**
 * Utilitário unificado para aplicar sinais ópticos aos nós e edges dos diagramas
 * (Caixa e Armário). Usado para evitar duplicação de getOpticalSignals + atualização de estado.
 */

import type { Dispatch, SetStateAction } from 'react';

export type SignalsDataMap = Record<string, { outputSignals?: Record<string, number> } | null> | null;

type NodeWithData = { id: string; data: Record<string, unknown>; [k: string]: unknown };
type EdgeWithSource = { id: string; source: string; sourceHandle?: string; [k: string]: unknown };

/**
 * Aplica sinais ópticos a uma lista de nós (atualiza data.opticalSignal).
 */
export function applySignalsToNodes<N extends NodeWithData>(
  nodes: N[],
  signalsData: SignalsDataMap
): N[] {
  return nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      opticalSignal: signalsData && signalsData[node.id] ? signalsData[node.id] : null
    }
  }));
}

/**
 * Calcula se uma edge tem sinal a partir do source e sourceHandle.
 */
function edgeHasSignal(
  edge: EdgeWithSource,
  signalsData: SignalsDataMap
): boolean {
  const entry = signalsData?.[edge.source];
  const outSignals = entry?.outputSignals;
  if (!outSignals) return false;
  const signalVal = outSignals[edge.sourceHandle || 'output-0'];
  return signalVal !== undefined && signalVal !== null;
}

/**
 * Aplica sinais ópticos a uma lista de edges (atualiza animated).
 */
export function applySignalsToEdges<E extends EdgeWithSource>(
  edges: E[],
  signalsData: SignalsDataMap
): (E & { animated: boolean })[] {
  return edges.map((edge) => ({
    ...edge,
    animated: edgeHasSignal(edge, signalsData)
  }));
}

/**
 * Atualiza o estado do React Flow (setNodes, setEdges) com os sinais ópticos.
 * Usar após obter signalsData (ex.: getOpticalSignals). Funcional para uso em callbacks.
 */
export function applySignalsToState(
  signalsData: SignalsDataMap,
  setNodes: Dispatch<SetStateAction<any[]>>,
  setEdges: Dispatch<SetStateAction<any[]>>
): void {
  setNodes((currentNodes) => applySignalsToNodes(currentNodes, signalsData));
  setEdges((currentEdges) => applySignalsToEdges(currentEdges, signalsData));
}
